import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ChevronRight, Clock, ArrowLeft, ArrowRight, Twitter, Linkedin, Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCms } from "@/lib/cms";
import { formatPublishDate, resolveMediaUrl, toEmbedUrl } from "@/lib/podcast";
import PodcastAudioPlayer from "@/components/PodcastAudioPlayer";

export default function PodcastArticle() {
  const { site } = useCms();
  const brandName = site?.company_name || "";
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Unchanged — same endpoint, same fetch/error handling as before.
  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/podcast/posts/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  // Unchanged — basic SEO tag handling, no helmet-style library in this project.
  useEffect(() => {
    const post = data?.post;
    if (!post) return;
    const prevTitle = document.title;
    document.title = post.seo_title || `${post.title} | ${brandName} Podcast`;
    let meta = document.querySelector('meta[name="description"]');
    const prevDescription = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", post.seo_description || post.excerpt || "");
    return () => {
      document.title = prevTitle;
      if (meta && prevDescription != null) meta.setAttribute("content", prevDescription);
    };
  }, [data, brandName]);

  if (loading) {
    return (
      <div style={{ backgroundColor: "#F8FAFC" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-24 animate-pulse space-y-4" data-testid="podcast-article-skeleton">
          <div className="h-3 w-24 bg-gray-200 rounded" />
          <div className="h-10 w-3/4 bg-gray-200 rounded" />
          <div className="aspect-[16/9] w-full bg-gray-200 rounded-2xl mt-6" />
        </div>
      </div>
    );
  }
  if (notFound || !data?.post) {
    return (
      <div style={{ backgroundColor: "#F8FAFC" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-24 text-center">
          <div className="rebrand-serif text-2xl font-semibold" style={{ color: "#1A202C" }}>Post not found.</div>
          <Link to="/podcast" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium hover:underline" style={{ color: "#3B68E7" }}>
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to Podcast
          </Link>
        </div>
      </div>
    );
  }

  const { post, related, prev, next } = data;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    toast.success("Link copied");
  };

  return (
    <article data-testid="podcast-article-page" style={{ backgroundColor: "#F8FAFC" }}>
      {/* Breadcrumb */}
      <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 pt-8">
        <nav className="flex items-center gap-1.5 text-xs" style={{ color: "#64748B" }} aria-label="Breadcrumb">
          <Link to="/" className="hover:underline" style={{ color: "#64748B" }}>Home</Link>
          <ChevronRight className="w-3 h-3" aria-hidden="true" />
          <Link to="/podcast" className="hover:underline" style={{ color: "#64748B" }}>Podcast</Link>
          <ChevronRight className="w-3 h-3" aria-hidden="true" />
          <span className="font-medium truncate max-w-[200px]" style={{ color: "#1A202C" }}>{post.title}</span>
        </nav>
      </div>

      {/* Header */}
      <header className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 pt-6">
        {post.category && (
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#3B68E7" }}>{post.category}</div>
        )}
        <h1 className="rebrand-serif tracking-tight font-bold leading-[1.05]" style={{ fontSize: "clamp(2rem, 4vw + 1rem, 3rem)", color: "#1A202C" }}>
          {post.title}
        </h1>
        <div className="mt-5 flex flex-wrap items-center gap-3 text-sm" style={{ color: "#64748B" }}>
          {post.author && <span className="font-medium" style={{ color: "#1A202C" }}>{post.author}</span>}
          {post.publish_date && (<><span style={{ color: "#CBD5E1" }}>·</span><span>{formatPublishDate(post.publish_date)}</span></>)}
          <span style={{ color: "#CBD5E1" }}>·</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" aria-hidden="true" /> {post.reading_time} min read</span>
        </div>
      </header>

      {/* Hero image */}
      {post.featured_image && (
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 mt-8">
          <img
            src={resolveMediaUrl(post.featured_image)} alt={post.title}
            className="w-full aspect-[16/9] object-cover"
            style={{ borderRadius: 24, border: "1px solid #E2E8F0" }}
          />
        </div>
      )}

      {/* Audio player — same audio_url, same file serving, richer control surface */}
      {post.audio_url && (
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 mt-8">
          <PodcastAudioPlayer src={resolveMediaUrl(post.audio_url)} downloadName={`${post.slug}.mp3`} />
        </div>
      )}

      {/* Rich content */}
      <div
        className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 mt-10 prose prose-slate prose-headings:font-['Playfair_Display'] prose-headings:tracking-tight prose-headings:text-[#1A202C] prose-a:text-[#3B68E7] prose-img:rounded-2xl prose-img:border prose-img:border-[#E2E8F0]"
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content || ""}</ReactMarkdown>
      </div>

      {/* Embedded videos */}
      {post.embedded_videos?.length > 0 && (
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 mt-10 space-y-6">
          {post.embedded_videos.map((v, i) => {
            const embed = toEmbedUrl(v);
            if (!embed) return null;
            return (
              <div key={i} className="aspect-video overflow-hidden bg-black" style={{ borderRadius: 24, border: "1px solid #E2E8F0" }}>
                <iframe
                  src={embed} title={`${post.title} video ${i + 1}`} className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Gallery */}
      {post.gallery?.length > 0 && (
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 mt-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {post.gallery.map((img, i) => (
              <img
                key={i} src={resolveMediaUrl(img)} alt={`${post.title} ${i + 1}`} loading="lazy"
                className="w-full aspect-[4/3] object-cover" style={{ borderRadius: 16, border: "1px solid #E2E8F0" }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Tags + share */}
      <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 mt-12 pt-8 flex flex-wrap items-center justify-between gap-4" style={{ borderTop: "1px solid #E2E8F0" }}>
        <div className="flex flex-wrap gap-2">
          {post.tags?.map((t) => (
            <span key={t} className="px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: "#F1F5F9", color: "#475569" }}>#{t}</span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
            target="_blank" rel="noopener noreferrer" aria-label="Share on Twitter"
            className="w-9 h-9 grid place-items-center rounded-full transition-colors hover:bg-gray-50"
            style={{ border: "1px solid #E2E8F0" }}
          >
            <Twitter className="w-4 h-4" style={{ color: "#64748B" }} aria-hidden="true" />
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
            target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn"
            className="w-9 h-9 grid place-items-center rounded-full transition-colors hover:bg-gray-50"
            style={{ border: "1px solid #E2E8F0" }}
          >
            <Linkedin className="w-4 h-4" style={{ color: "#64748B" }} aria-hidden="true" />
          </a>
          <button
            onClick={copyLink} aria-label="Copy link"
            className="w-9 h-9 grid place-items-center rounded-full transition-colors hover:bg-gray-50"
            style={{ border: "1px solid #E2E8F0" }}
          >
            <LinkIcon className="w-4 h-4" style={{ color: "#64748B" }} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Prev / Next */}
      {(prev || next) && (
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 mt-8 grid sm:grid-cols-2 gap-4">
          {prev ? (
            <button
              onClick={() => navigate(`/podcast/${prev.slug}`)}
              className="text-left p-4 transition-all hover:shadow-sm"
              style={{ borderRadius: 16, border: "1px solid #E2E8F0" }}
            >
              <div className="text-xs flex items-center gap-1" style={{ color: "#64748B" }}><ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" /> Previous</div>
              <div className="mt-1 font-medium line-clamp-1" style={{ color: "#1A202C" }}>{prev.title}</div>
            </button>
          ) : <div />}
          {next ? (
            <button
              onClick={() => navigate(`/podcast/${next.slug}`)}
              className="text-right p-4 transition-all hover:shadow-sm"
              style={{ borderRadius: 16, border: "1px solid #E2E8F0" }}
            >
              <div className="text-xs flex items-center justify-end gap-1" style={{ color: "#64748B" }}>Next <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" /></div>
              <div className="mt-1 font-medium line-clamp-1" style={{ color: "#1A202C" }}>{next.title}</div>
            </button>
          ) : <div />}
        </div>
      )}

      {/* Related posts */}
      {related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 mt-16 pt-12 pb-24" style={{ borderTop: "1px solid #E2E8F0" }}>
          <h2 className="rebrand-serif text-xl sm:text-2xl font-bold tracking-tight mb-6" style={{ color: "#1A202C" }}>Related posts</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug} to={`/podcast/${r.slug}`}
                className="group block overflow-hidden bg-white transition-all hover:-translate-y-1 hover:shadow-[0_14px_35px_rgba(0,0,0,0.08)]"
                style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}
              >
                <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                  <img
                    src={resolveMediaUrl(r.featured_image)} alt={r.title} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                  />
                </div>
                <div className="p-4">
                  <h3 className="rebrand-serif text-base font-semibold line-clamp-2" style={{ color: "#1A202C" }}>{r.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
